import { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  ArchiveBoxArrowDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useSchool } from '@/contexts/SchoolContext';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import {
  subscribeCurrencies,
  createCurrency,
  updateCurrency,
  deactivateCurrency,
} from '@/services/currencyService';

/**
 * CurrenciesPanel — substrate slide-out for *currency setup*.
 *
 * Currencies are units of value (points, merits, stars) that domains can
 * award. Setting `groupBy: 'house'` on a currency makes it eligible to feed
 * the House Competition (wiring happens in CompetitionsPanel; this panel
 * just owns the currency CRUD).
 */

const GROUP_BY_OPTIONS = [
  { value: '', label: 'Nothing (no aggregation)' },
  { value: 'house', label: 'House Competition' },
];

const groupLabel = (groupBy) => {
  if (!groupBy) return 'Nothing';
  if (groupBy === 'house') return 'House Competition';
  return groupBy;
};

const emptyDraft = {
  label: '',
  unit: '',
  unitPlural: '',
  groupBy: '',
};

export default function CurrenciesPanel() {
  const { schoolId } = useSchool();
  const { currentRole } = useRoleSwitcher();
  const isAdmin = currentRole === 'admin' || currentRole === 'dev';

  const [currencies, setCurrencies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!schoolId) {
      setCurrencies([]);
      setLoaded(true);
      return undefined;
    }
    setLoaded(false);
    const unsub = subscribeCurrencies(schoolId, (rows) => {
      setCurrencies(rows);
      setLoaded(true);
    });
    return unsub;
  }, [schoolId]);

  if (!isAdmin) {
    return (
      <div className="p-4 text-sm text-base-content/60">
        Currencies are admin-only. Switch to your admin role to manage them.
      </div>
    );
  }

  const startCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft);
    setError(null);
    setShowCreate(true);
  };

  const startEdit = (currency) => {
    setShowCreate(false);
    setEditingId(currency.id);
    setDraft({
      label: currency.label || '',
      unit: currency.unit || '',
      unitPlural: currency.unitPlural || '',
      groupBy: currency.groupBy || '',
    });
    setError(null);
  };

  const cancel = () => {
    setShowCreate(false);
    setEditingId(null);
    setDraft(emptyDraft);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!draft.label.trim()) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        label: draft.label.trim(),
        unit: draft.unit.trim() || 'point',
        unitPlural: draft.unitPlural.trim() || `${draft.unit.trim() || 'point'}s`,
        groupBy: draft.groupBy || null,
      };
      if (editingId) {
        await updateCurrency(schoolId, editingId, payload);
      } else {
        await createCurrency(schoolId, { ...payload, name: draft.label.trim() });
      }
      cancel();
    } catch (err) {
      setError(err.message || 'Could not save currency.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (currency) => {
    if (!window.confirm(`Deactivate "${currency.label}"? Past totals are kept, but no new awards will write to this currency.`)) {
      return;
    }
    try {
      await deactivateCurrency(schoolId, currency.id);
    } catch (err) {
      setError(err.message || 'Could not deactivate currency.');
    }
  };

  return (
    <div className="p-4 pb-24 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Currencies</h3>
          <p className="text-xs text-base-content/60 truncate">
            {currencies.length} active · {currencies.filter((c) => c.groupBy === 'house').length} feed house competition
          </p>
        </div>
        {!showCreate && !editingId && (
          <button
            type="button"
            className="btn btn-primary btn-xs gap-1"
            onClick={startCreate}
            disabled={!schoolId}
          >
            <PlusIcon className="w-3.5 h-3.5" />
            New
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error text-xs py-2">
          <span>{error}</span>
        </div>
      )}

      {(showCreate || editingId) && (
        <form
          onSubmit={handleSubmit}
          className="card bg-base-100 border border-base-300 p-3 space-y-3"
        >
          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-xs font-medium text-base-content/70">Name *</legend>
            <input
              type="text"
              className="input input-sm w-full"
              placeholder="e.g. Recognition Points"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              required
              autoFocus
            />
          </fieldset>

          <div className="grid grid-cols-2 gap-2">
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-xs font-medium text-base-content/70">Unit (singular)</legend>
              <input
                type="text"
                className="input input-sm w-full"
                placeholder="point"
                value={draft.unit}
                onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))}
              />
            </fieldset>
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-xs font-medium text-base-content/70">Unit (plural)</legend>
              <input
                type="text"
                className="input input-sm w-full"
                placeholder="points"
                value={draft.unitPlural}
                onChange={(e) => setDraft((d) => ({ ...d, unitPlural: e.target.value }))}
              />
            </fieldset>
          </div>

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-xs font-medium text-base-content/70">Contributes to</legend>
            <select
              className="select select-sm w-full"
              value={draft.groupBy}
              onChange={(e) => setDraft((d) => ({ ...d, groupBy: e.target.value }))}
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-base-content/60 leading-snug">
              Currencies set to <strong>House Competition</strong> are auto-added as contributors. Weighting happens on the competition panel.
            </p>
          </fieldset>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn btn-ghost btn-sm" onClick={cancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm gap-1" disabled={submitting || !draft.label.trim()}>
              {submitting ? <span className="loading loading-spinner loading-xs" /> : <CheckIcon className="w-3.5 h-3.5" />}
              {editingId ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {!loaded && (
        <p className="text-xs text-base-content/40 py-6 text-center">Loading…</p>
      )}

      {loaded && currencies.length === 0 && !showCreate && (
        <p className="text-sm text-base-content/50 py-6 text-center">
          No currencies yet. Click <strong>New</strong> to create one.
        </p>
      )}

      {loaded && currencies.length > 0 && (
        <ul className="space-y-2">
          {currencies.map((c) => {
            const isEditing = editingId === c.id;
            if (isEditing) return null;
            return (
              <li
                key={c.id}
                className="card bg-base-100 border border-base-300 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{c.label}</span>
                      {c.groupBy === 'house' && (
                        <span className="badge badge-primary badge-sm">House</span>
                      )}
                    </div>
                    <p className="text-[11px] text-base-content/60 mt-0.5">
                      {c.unit || 'point'} / {c.unitPlural || 'points'} · contributes to {groupLabel(c.groupBy)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-none">
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square"
                      onClick={() => startEdit(c)}
                      aria-label={`Edit ${c.label}`}
                      title="Edit"
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square text-error"
                      onClick={() => handleDeactivate(c)}
                      aria-label={`Deactivate ${c.label}`}
                      title="Deactivate"
                    >
                      <ArchiveBoxArrowDownIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
